import i18n from 'i18next';
import {initReactI18next} from 'react-i18next';

import enSit from './locales/en/sit.json';
import {EXTRA_LOCALES} from './customers/active-locales';
import {C} from "~/configurations/active-configuration";

/**
 * App-wide i18n setup.
 *
 * `en` is always available. A tenant adds further locales by dropping translation files under
 * `src/i18n/customers/<customer>/<locale>/sit.json` (see `customers/compass/pt-BR`) — no code
 * change needed. `tools/applyConfig.cjs` reads `VITE_CUSTOMER` and writes whichever folder
 * matches into the gitignored `./customers/active-locales`, so other tenants' builds never
 * bundle another customer's translations (or their name). The first extra locale found becomes
 * the tenant's default; a user can still override at runtime via the SIT settings language
 * switch, which persists to localStorage and takes precedence.
 */
export const SUPPORTED_LOCALES = ['en', ...Object.keys(EXTRA_LOCALES)] as const;
export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

const LOCALE_STORAGE_KEY = 'sit-locale';
const isSupported = (v: string | null | undefined): v is SupportedLocale =>
    (SUPPORTED_LOCALES as readonly string[]).includes(v ?? '');

const storedLocale = typeof localStorage !== 'undefined' ? localStorage.getItem(LOCALE_STORAGE_KEY) : null;
const fallbackLng = 'en';
const defaultLng = Object.keys(EXTRA_LOCALES)[0] ?? fallbackLng;
const lng: SupportedLocale = isSupported(storedLocale) ? storedLocale : defaultLng;

/** Current active locale. */
export const getLocale = (): SupportedLocale => isSupported(i18n.language) ? i18n.language : fallbackLng;

/** Persist the user's language choice and reload so all labels re-resolve. */
export function setLocale(locale: SupportedLocale): void {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    void i18n.changeLanguage(locale);
    window.location.reload();
}

void i18n
    .use(initReactI18next)
    .init({
        lng,
        fallbackLng,
        ns: ['sit'],
        defaultNS: 'sit',
        resources: {
            'en': {sit: enSit},
            ...EXTRA_LOCALES,
        },
        interpolation: {
            defaultVariables: {
                mithraBranding: C.profile.branding ?? '',
                mithraBrandingWithParenthesis: C.profile.branding ? '(' + C.profile.branding + ')' : '',
            },
            escapeValue: false
        }, // React already escapes
        returnNull: false,
    });

export default i18n;
